import dayjs, { Dayjs } from 'dayjs'
import { ReactNode, useMemo, useState } from 'react'
import { Trans } from '@lingui/macro'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DoneIcon from '@mui/icons-material/Done'
import UndoIcon from '@mui/icons-material/Undo'
import ClearIcon from '@mui/icons-material/Clear'
import EventRepeatIcon from '@mui/icons-material/EventRepeat'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import { Planning, ShiftProposal, ShiftProposalStatus } from '@/core/models/planning.model'
import { ScheduleRecurrenceType } from '@/core/models/schedule.model'
import { toAdapterLocale } from '@/utils/locale'
import {
  useApproveShiftProposalMutation,
  useCreateShiftProposalMutation,
  useGetMyShiftProposalsQuery,
  useGetPendingShiftProposalsQuery,
  useRejectShiftProposalMutation,
  useWithdrawShiftProposalMutation,
} from '@/core/services/planning'
import { useCreateSpecialityOptionMutation, useGetSpecialityOptionsQuery } from '@/core/services/speciality-option'
import SpecialitiesAutoComplete, { SpecialityAutocompleteOption } from '@/components/ui/autocomplete/SpecialitiesAutoComplete'
import { DatePickerInput } from '@/components/ui/inputs/DatePickerInput'
import TimePickerInput from '@/components/ui/inputs/TimePickerInput'
import { getApiErrorMessage } from '@/utils/errors'
import { browserTimezone } from '@/utils/date'
import { useIsMobile } from '@/hooks/useSizeBreakpoints'

type ShiftProposalsProps = {
  canManagePlanning: boolean
  planning: Planning
}

export function ShiftProposals({ canManagePlanning, planning }: ShiftProposalsProps) {
  const isMobile = useIsMobile()

  if (isMobile) {
    return <MobileShiftProposals planning={planning} canManagePlanning={canManagePlanning} />
  }

  return (
    <Stack spacing={2} sx={{ mb: 2 }} data-testid="shift-proposals">
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>
              <Trans>Shift proposals</Trans>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <Trans>Propose one-off or recurring shifts and track their review status.</Trans>
            </Typography>
          </Box>
          <ProposeShiftDialog planning={planning} />
        </Stack>
      </Paper>

      {canManagePlanning && <PendingShiftProposals planningId={planning._id} />}
      <MyShiftProposals planningId={planning._id} />
    </Stack>
  )
}

function MobileShiftProposals({ canManagePlanning, planning }: ShiftProposalsProps) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const {
    data: pendingProposals = [],
    isLoading: isLoadingPendingProposals,
    isError: hasPendingProposalsError,
  } = useGetPendingShiftProposalsQuery(planning._id, { skip: !canManagePlanning })
  const { data: myProposals = [], isLoading: isLoadingMyProposals, isError: hasMyProposalsError } = useGetMyShiftProposalsQuery(planning._id)

  const pendingCount = canManagePlanning && !hasPendingProposalsError ? pendingProposals.length : 0
  const myCount = hasMyProposalsError ? 0 : myProposals.length
  const title = t('page.planning.proposals.mobileTitle', { defaultValue: 'Proposals' })
  const pendingLabel = t('page.planning.proposals.pendingShort', { defaultValue: 'Pending' })
  const myLabel = t('page.planning.proposals.myShort', { defaultValue: 'Mine' })
  const toggleLabel = expanded
    ? t('page.planning.proposals.collapseDetails', { defaultValue: 'Hide proposal details' })
    : t('page.planning.proposals.expandDetails', { defaultValue: 'Show proposal details' })

  return (
    <Paper data-testid="shift-proposals" variant="outlined" sx={{ mb: 1, overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" gap={0.75} sx={{ p: 1 }}>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2} noWrap>
            {title}
          </Typography>
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ mt: 0.5, minWidth: 0 }}>
            {canManagePlanning && <ProposalCountChip count={pendingCount} loading={isLoadingPendingProposals} label={pendingLabel} />}
            <ProposalCountChip count={myCount} loading={isLoadingMyProposals} label={myLabel} />
          </Stack>
        </Box>
        <ProposeShiftDialog planning={planning} />
        <Tooltip title={toggleLabel}>
          <IconButton
            aria-expanded={expanded}
            aria-label={toggleLabel}
            data-testid="toggle-shift-proposals"
            onClick={() => setExpanded((current) => !current)}
            size="small"
            sx={{ flexShrink: 0 }}
          >
            <ExpandMoreIcon
              fontSize="small"
              sx={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: (theme) => theme.transitions.create('transform'),
              }}
            />
          </IconButton>
        </Tooltip>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Divider />
        <Stack spacing={1.25} sx={{ p: 1.25 }}>
          {canManagePlanning && !hasPendingProposalsError && (
            <ProposalListSection
              title={<Trans>Pending proposals</Trans>}
              count={pendingProposals.length}
              loading={isLoadingPendingProposals}
              testId="pending-shift-proposals"
            >
              {pendingProposals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  <Trans>No pending proposals.</Trans>
                </Typography>
              ) : (
                <List disablePadding>
                  {pendingProposals.map((proposal, index) => (
                    <ProposalListItem
                      key={proposal._id}
                      proposal={proposal}
                      showProposer
                      actions={<ManagerProposalActions planningId={planning._id} proposal={proposal} />}
                      divider={index < pendingProposals.length - 1}
                    />
                  ))}
                </List>
              )}
            </ProposalListSection>
          )}
          {!hasMyProposalsError && (
            <ProposalListSection title={<Trans>My proposals</Trans>} count={myProposals.length} loading={isLoadingMyProposals} testId="my-shift-proposals">
              {myProposals.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  <Trans>You have not proposed any shifts for this planning.</Trans>
                </Typography>
              ) : (
                <List disablePadding>
                  {myProposals.map((proposal, index) => (
                    <ProposalListItem
                      key={proposal._id}
                      proposal={proposal}
                      actions={proposal.status === 'pending' ? <WithdrawProposalAction planningId={planning._id} proposal={proposal} /> : null}
                      divider={index < myProposals.length - 1}
                    />
                  ))}
                </List>
              )}
            </ProposalListSection>
          )}
        </Stack>
      </Collapse>
    </Paper>
  )
}

function ProposalCountChip({ count, loading, label }: { count: number; loading?: boolean; label: string }) {
  const value = loading ? '...' : count

  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${label} ${value}`}
      sx={{
        height: 22,
        maxWidth: '12ch',
        '& .MuiChip-label': {
          fontSize: '0.72rem',
          lineHeight: 1.1,
          overflow: 'hidden',
          px: 0.75,
          textOverflow: 'ellipsis',
        },
      }}
    />
  )
}

function ProposalListSection({
  title,
  count,
  loading,
  testId,
  children,
}: {
  title: ReactNode
  count: number
  loading?: boolean
  testId: string
  children: ReactNode
}) {
  return (
    <Box data-testid={testId}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} sx={{ mb: 0.75 }}>
        <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ minWidth: 0 }}>
          {title}
        </Typography>
        <Chip size="small" label={loading ? '...' : count} sx={{ height: 22, flexShrink: 0 }} />
      </Stack>
      {children}
    </Box>
  )
}

function ProposeShiftDialog({ planning }: { planning: Planning }) {
  const { enqueueSnackbar } = useSnackbar()
  const { t, i18n } = useTranslation()
  const isMobile = useIsMobile()
  const [open, setOpen] = useState(false)
  const [date, setDate] = useState<Dayjs | null>(() => getDefaultProposalDate(planning))
  const [startTime, setStartTime] = useState<Dayjs | null>(() => dayjs().hour(9).minute(0).second(0).millisecond(0))
  const [endTime, setEndTime] = useState<Dayjs | null>(() => dayjs().hour(12).minute(0).second(0).millisecond(0))
  const [location, setLocation] = useState('')
  const [consultationReason, setConsultationReason] = useState('')
  const [note, setNote] = useState('')
  const [isRecurring, setIsRecurring] = useState(false)
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([])
  const [recurrenceEnd, setRecurrenceEnd] = useState<Dayjs | null>(null)
  const [createShiftProposal, { isLoading }] = useCreateShiftProposalMutation()
  const triggerLabel = t('page.planning.proposals.create.action', { defaultValue: 'Propose a shift' })

  // Weekday choices in Monday-first order; value = JS getDay (0=Sun..6=Sat) to match the
  // backend recurrence generator and the shift-template form.
  const weekdayOptions = useMemo(() => {
    const formatter = new Intl.DateTimeFormat(toAdapterLocale(i18n.language), { weekday: 'short', timeZone: 'UTC' })
    return [1, 2, 3, 4, 5, 6, 0].map((value) => ({ value, label: formatter.format(new Date(Date.UTC(2023, 0, 1 + value))) }))
  }, [i18n.language])

  const { startDateTime, endDateTime, hasInvalidTime } = useMemo(() => {
    if (!date || !startTime || !endTime) {
      return { startDateTime: null, endDateTime: null, hasInvalidTime: true }
    }
    const start = date.hour(startTime.hour()).minute(startTime.minute()).second(0).millisecond(0)
    const end = date.hour(endTime.hour()).minute(endTime.minute()).second(0).millisecond(0)
    return { startDateTime: start, endDateTime: end, hasInvalidTime: !end.isAfter(start) }
  }, [date, startTime, endTime])

  const toggleRecurring = (checked: boolean) => {
    setIsRecurring(checked)
    if (checked) {
      if (daysOfWeek.length === 0 && date) setDaysOfWeek([date.day()])
      if (!recurrenceEnd) setRecurrenceEnd(dayjs(planning.endDate))
    }
  }

  const reset = () => {
    setDate(getDefaultProposalDate(planning))
    setStartTime(dayjs().hour(9).minute(0).second(0).millisecond(0))
    setEndTime(dayjs().hour(12).minute(0).second(0).millisecond(0))
    setLocation('')
    setConsultationReason('')
    setNote('')
    setIsRecurring(false)
    setDaysOfWeek([])
    setRecurrenceEnd(null)
  }

  const submit = async () => {
    if (!startDateTime || !endDateTime || hasInvalidTime || !location.trim() || !consultationReason.trim()) {
      enqueueSnackbar(t('page.planning.proposals.create.invalid', { defaultValue: 'Please complete the proposal fields.' }), { variant: 'warning' })
      return
    }
    if (isRecurring && (daysOfWeek.length === 0 || !recurrenceEnd)) {
      enqueueSnackbar(t('page.planning.proposals.create.invalidRecurrence', { defaultValue: 'Pick at least one weekday and a repeat-until date.' }), { variant: 'warning' })
      return
    }

    try {
      await createShiftProposal({
        planningId: planning._id,
        // True instants: the picked local times as-is. `timezone` tells the backend which
        // zone anchors the recurring template's time-of-day; every client renders the
        // booked shift on the viewer's own clock.
        startTime: startDateTime.toISOString(),
        endTime: endDateTime.toISOString(),
        timezone: browserTimezone(),
        location: location.trim(),
        consultationReason: consultationReason.trim(),
        note: note.trim() || undefined,
        ...(isRecurring && recurrenceEnd
          ? {
              recurrenceEndDate: recurrenceEnd.utc(true).startOf('day').toISOString(),
              recurrenceRule: {
                daysOfWeek,
                datesOfMonth: [],
                monthsOfYear: [],
                recurrenceType: ScheduleRecurrenceType.WEEKLY,
                separationCount: 1,
              },
            }
          : {}),
      }).unwrap()
      enqueueSnackbar(t('page.planning.proposals.create.success', { defaultValue: 'Shift proposal submitted.' }), { variant: 'success' })
      setOpen(false)
      reset()
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.planning.proposals.create.error', { defaultValue: 'Could not submit the shift proposal.' })), {
        variant: 'error',
      })
    }
  }

  return (
    <>
      {isMobile ? (
        <Tooltip title={triggerLabel}>
          <IconButton
            aria-label={triggerLabel}
            color="primary"
            data-testid="open-shift-proposal-dialog"
            onClick={() => setOpen(true)}
            size="small"
            sx={{
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              flexShrink: 0,
              '&:hover': { bgcolor: 'primary.dark' },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ) : (
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)} size="small" data-testid="open-shift-proposal-dialog" sx={{ flexShrink: 0 }}>
          <Trans>Propose a shift</Trans>
        </Button>
      )}
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Dialog open={open} onClose={() => setOpen(false)} fullScreen={isMobile} fullWidth maxWidth="sm" scroll="paper" PaperProps={{ 'data-testid': 'shift-proposal-dialog' }}>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Trans>Propose a shift</Trans>
            <Tooltip title={t('common:button.close.tooltip')}>
              <IconButton onClick={() => setOpen(false)}>
                <CloseRoundedIcon />
              </IconButton>
            </Tooltip>
          </DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <DatePickerInput fullWidth label={t('page.planning.proposals.date', { defaultValue: 'Date' })} value={date} onChange={(value) => setDate(value)} />
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TimePickerInput
                  fullWidth
                  inputFormat="HH:mm"
                  mask="__:__"
                  label={t('page.planning.proposals.startTime', { defaultValue: 'Start time' })}
                  value={startTime}
                  onChange={(value) => setStartTime(dayjs.isDayjs(value) ? value : null)}
                />
                <TimePickerInput
                  fullWidth
                  inputFormat="HH:mm"
                  mask="__:__"
                  label={t('page.planning.proposals.endTime', { defaultValue: 'End time' })}
                  value={endTime}
                  onChange={(value) => setEndTime(dayjs.isDayjs(value) ? value : null)}
                />
              </Stack>
              {hasInvalidTime && (
                <Alert severity="warning">
                  <Trans>End time must be after start time.</Trans>
                </Alert>
              )}
              <TextField fullWidth label={t('page.planning.proposals.location', { defaultValue: 'Location' })} value={location} onChange={(event) => setLocation(event.target.value)} />
              <ConsultationReasonSelector value={consultationReason} onChange={setConsultationReason} />
              <TextField
                fullWidth
                multiline
                minRows={3}
                label={t('page.planning.proposals.note', { defaultValue: 'Optional note' })}
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
              <Divider />
              <FormControlLabel
                control={<Checkbox checked={isRecurring} onChange={(event) => toggleRecurring(event.target.checked)} />}
                label={t('page.planning.proposals.recurring', { defaultValue: 'Repeat weekly' })}
                data-testid="shift-proposal-repeat-weekly"
              />
              {isRecurring && (
                <Stack spacing={1.5}>
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      {t('page.planning.proposals.repeatOn', { defaultValue: 'Repeat on' })}
                    </Typography>
                    <ToggleButtonGroup value={daysOfWeek} onChange={(_event, value) => setDaysOfWeek(value as number[])} size="small" sx={{ flexWrap: 'wrap' }} data-testid="shift-proposal-weekdays">
                      {weekdayOptions.map((option) => (
                        <ToggleButton key={option.value} value={option.value} sx={{ textTransform: 'none', px: 1.5 }}>
                          {option.label}
                        </ToggleButton>
                      ))}
                    </ToggleButtonGroup>
                  </Box>
                  <DatePickerInput
                    fullWidth
                    label={t('page.planning.proposals.repeatUntil', { defaultValue: 'Repeat until' })}
                    value={recurrenceEnd}
                    onChange={(value) => setRecurrenceEnd(value)}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {t('page.planning.proposals.recurringHint', { defaultValue: 'When approved, you will be booked onto every matching day in this range.' })}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setOpen(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <LoadingButton loading={isLoading} variant="contained" onClick={submit}>
              <Trans>Submit</Trans>
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </>
  )
}

function ConsultationReasonSelector({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useTranslation()
  const [searchInput, setSearchInput] = useState<string>()
  const { data: specialities = [] } = useGetSpecialityOptionsQuery(searchInput ? { smartSearch: searchInput } : {}, {
    selectFromResult: ({ data }) => ({ data: data ?? [] }),
  })
  const [createSpecialityOption] = useCreateSpecialityOptionMutation()

  const selectedSpeciality: SpecialityAutocompleteOption | null = value ? { _id: '', name: value, code: '' } : null

  const createAndSelect = async (rawName: string) => {
    const name = rawName.trim()
    if (!name) return
    try {
      const created = await createSpecialityOption({ name, code: 'CREATED.' + name.toLowerCase().replace(/ /g, '-') }).unwrap()
      onChange(created?.name ?? name)
    } catch {
      onChange(name)
    }
  }

  return (
    <SpecialitiesAutoComplete
      id="shift-proposal-consultation-reason"
      name="consultationReason"
      label={t('page.planning.proposals.specialityReason', { defaultValue: 'Speciality / reason' })}
      editable
      canDelete
      multiple={false}
      selectedSpecialities={selectedSpeciality}
      specialities={specialities}
      onInputChange={setSearchInput}
      onSpecialityChange={(_event, newValue, reason) => {
        if (Array.isArray(newValue)) return
        if (reason === 'clear' || reason === 'removeOption' || !newValue) {
          onChange('')
          return
        }
        if (typeof newValue === 'string') {
          createAndSelect(newValue)
          return
        }
        if (newValue.isNew && newValue.inputValue) {
          createAndSelect(newValue.inputValue)
          return
        }
        onChange(newValue.name)
      }}
    />
  )
}

function ProposalSection({
  title,
  count,
  loading,
  testId,
  children,
}: {
  title: ReactNode
  count: number
  loading?: boolean
  testId: string
  children: ReactNode
}) {
  const countChip = <Chip size="small" label={loading ? '...' : count} />

  return (
    <Paper sx={{ p: 2 }} data-testid={testId}>
      <Stack spacing={1.5}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
          <Typography variant="h6">{title}</Typography>
          {countChip}
        </Stack>
        {children}
      </Stack>
    </Paper>
  )
}

function PendingShiftProposals({ planningId }: { planningId: string }) {
  const { data: proposals = [], isLoading, isError } = useGetPendingShiftProposalsQuery(planningId)

  if (isError) return null

  return (
    <ProposalSection title={<Trans>Pending proposals</Trans>} count={proposals.length} loading={isLoading} testId="pending-shift-proposals">
      {proposals.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          <Trans>No pending proposals.</Trans>
        </Typography>
      ) : (
        <List disablePadding>
          {proposals.map((proposal, index) => (
            <ProposalListItem key={proposal._id} proposal={proposal} showProposer actions={<ManagerProposalActions planningId={planningId} proposal={proposal} />} divider={index < proposals.length - 1} />
          ))}
        </List>
      )}
    </ProposalSection>
  )
}

function MyShiftProposals({ planningId }: { planningId: string }) {
  const { data: proposals = [], isLoading, isError } = useGetMyShiftProposalsQuery(planningId)

  if (isError) return null

  return (
    <ProposalSection title={<Trans>My proposals</Trans>} count={proposals.length} loading={isLoading} testId="my-shift-proposals">
      {proposals.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          <Trans>You have not proposed any shifts for this planning.</Trans>
        </Typography>
      ) : (
        <List disablePadding>
          {proposals.map((proposal, index) => (
            <ProposalListItem key={proposal._id} proposal={proposal} actions={proposal.status === 'pending' ? <WithdrawProposalAction planningId={planningId} proposal={proposal} /> : null} divider={index < proposals.length - 1} />
          ))}
        </List>
      )}
    </ProposalSection>
  )
}

function ProposalListItem({
  actions,
  divider,
  proposal,
  showProposer = false,
}: {
  actions?: ReactNode
  divider?: boolean
  proposal: ShiftProposal
  showProposer?: boolean
}) {
  const { i18n } = useTranslation()
  return (
    <>
      <ListItem
        disableGutters
        sx={{
          alignItems: 'flex-start',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          gap: { xs: 1, sm: 0 },
        }}
      >
        <ListItemText
          sx={{ mr: { xs: 0, sm: 2 } }}
          primary={
            <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1}>
              <Typography variant="subtitle2">{formatProposalTime(proposal)}</Typography>
              <StatusChip status={proposal.status} />
              {proposal.recurrenceRule && proposal.recurrenceEndDate && (
                <Chip size="small" variant="outlined" icon={<EventRepeatIcon fontSize="small" />} label={formatRecurrence(proposal, toAdapterLocale(i18n.language))} />
              )}
            </Stack>
          }
          secondary={
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {showProposer && (
                <Typography variant="body2" color="text.secondary">
                  {getStaffName(proposal.proposedBy)}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                {proposal.location} · {proposal.consultationReason}
              </Typography>
              {proposal.note && (
                <Typography variant="body2" color="text.secondary">
                  {proposal.note}
                </Typography>
              )}
              {proposal.reviewReason && (
                <Typography variant="body2" color="text.secondary">
                  {proposal.reviewReason}
                </Typography>
              )}
            </Stack>
          }
        />
        {actions && <Box sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>{actions}</Box>}
      </ListItem>
      {divider && <Divider />}
    </>
  )
}

function ManagerProposalActions({ planningId, proposal }: { planningId: string; proposal: ShiftProposal }) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const isMobile = useIsMobile()
  const [approveShiftProposal, { isLoading: isApproving }] = useApproveShiftProposalMutation()
  const [rejectShiftProposal, { isLoading: isRejecting }] = useRejectShiftProposalMutation()
  const [rejectOpen, setRejectOpen] = useState(false)
  const [reason, setReason] = useState('')

  const approve = async () => {
    try {
      await approveShiftProposal({ planningId, proposalId: proposal._id }).unwrap()
      enqueueSnackbar(t('page.planning.proposals.approve.success', { defaultValue: 'Shift proposal approved.' }), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.planning.proposals.approve.error', { defaultValue: 'Could not approve the shift proposal.' })), { variant: 'error' })
    }
  }

  const reject = async () => {
    try {
      await rejectShiftProposal({ planningId, proposalId: proposal._id, reason: reason.trim() || undefined }).unwrap()
      enqueueSnackbar(t('page.planning.proposals.reject.success', { defaultValue: 'Shift proposal rejected.' }), { variant: 'success' })
      setRejectOpen(false)
      setReason('')
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.planning.proposals.reject.error', { defaultValue: 'Could not reject the shift proposal.' })), { variant: 'error' })
    }
  }

  return (
    <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
      <LoadingButton loading={isApproving} size="small" variant="contained" startIcon={<DoneIcon />} onClick={approve} sx={{ flex: { xs: 1, sm: 'initial' } }}>
        <Trans>Approve</Trans>
      </LoadingButton>
      <Button size="small" variant="outlined" color="error" startIcon={<ClearIcon />} onClick={() => setRejectOpen(true)} sx={{ flex: { xs: 1, sm: 'initial' } }}>
        <Trans>Reject</Trans>
      </Button>
      <Dialog open={rejectOpen} onClose={() => setRejectOpen(false)} fullScreen={isMobile} fullWidth maxWidth="xs">
        <DialogTitle>
          <Trans>Reject proposal</Trans>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('page.planning.proposals.reject.reason', { defaultValue: 'Optional reason' })}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)}>
            <Trans>Cancel</Trans>
          </Button>
          <LoadingButton loading={isRejecting} color="error" variant="contained" onClick={reject}>
            <Trans>Reject</Trans>
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

function WithdrawProposalAction({ planningId, proposal }: { planningId: string; proposal: ShiftProposal }) {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()
  const [withdrawShiftProposal, { isLoading }] = useWithdrawShiftProposalMutation()

  const withdraw = async () => {
    try {
      await withdrawShiftProposal({ planningId, proposalId: proposal._id }).unwrap()
      enqueueSnackbar(t('page.planning.proposals.withdraw.success', { defaultValue: 'Shift proposal withdrawn.' }), { variant: 'success' })
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('page.planning.proposals.withdraw.error', { defaultValue: 'Could not withdraw the shift proposal.' })), { variant: 'error' })
    }
  }

  return (
    <LoadingButton loading={isLoading} size="small" variant="outlined" startIcon={<UndoIcon />} onClick={withdraw} sx={{ width: { xs: '100%', sm: 'auto' } }}>
      <Trans>Withdraw</Trans>
    </LoadingButton>
  )
}

function StatusChip({ status }: { status: ShiftProposalStatus }) {
  const { t } = useTranslation()
  const colorByStatus: Record<ShiftProposalStatus, 'default' | 'success' | 'error' | 'warning'> = {
    pending: 'warning',
    approved: 'success',
    rejected: 'error',
    withdrawn: 'default',
  }
  const labelByStatus: Record<ShiftProposalStatus, string> = {
    pending: t('page.planning.proposals.status.pending', { defaultValue: 'Pending' }),
    approved: t('page.planning.proposals.status.approved', { defaultValue: 'Approved' }),
    rejected: t('page.planning.proposals.status.rejected', { defaultValue: 'Rejected' }),
    withdrawn: t('page.planning.proposals.status.withdrawn', { defaultValue: 'Withdrawn' }),
  }

  return <Chip size="small" color={colorByStatus[status]} label={labelByStatus[status]} />
}

function getDefaultProposalDate(planning: Planning) {
  const today = dayjs()
  const start = dayjs(planning.startDate)
  return start.isAfter(today, 'day') ? start : today
}

function formatProposalTime(proposal: ShiftProposal) {
  // Proposals with a timezone hold true instants → render on the viewer's local clock.
  // Legacy proposals (no timezone) are wall-clock-as-UTC → render in UTC as before.
  if (proposal.timezone) {
    return `${dayjs(proposal.startTime).format('DD/MM/YYYY HH:mm')} - ${dayjs(proposal.endTime).format('HH:mm')}`
  }
  return `${dayjs.utc(proposal.startTime).format('DD/MM/YYYY HH:mm')} - ${dayjs.utc(proposal.endTime).format('HH:mm')}`
}

function formatRecurrence(proposal: ShiftProposal, locale: string): string {
  const rule = proposal.recurrenceRule
  if (!rule) return ''
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' })
  // daysOfWeek values are 0=Sun..6=Sat; display Monday-first. 2023-01-01 (UTC) is a Sunday.
  const days = [...rule.daysOfWeek]
    .sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))
    .map((value) => formatter.format(new Date(Date.UTC(2023, 0, 1 + value))))
    .join(', ')
  const until = proposal.recurrenceEndDate ? dayjs.utc(proposal.recurrenceEndDate).format('DD/MM/YYYY') : ''
  return until ? `${days} → ${until}` : days
}

function getStaffName(staff: ShiftProposal['proposedBy']) {
  if (!staff?.userInfo) return ''
  return `${staff.userInfo.name} ${staff.userInfo.surname}`
}
